import { useState, useEffect } from "react";
import { X, FilePdf, FileImage, FileDoc, FileText, ArrowSquareOut, Trash } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { api } from "@/lib/api";

const ICON_SIZE = 20;

function detectFileType(record) {
  const ct = (record.content_type || "").toLowerCase();
  const name = (record.file_name || "").toLowerCase();
  if (ct.startsWith("image/")) return "image";
  if (ct === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (ct.includes("word") || ct.includes("document") || name.endsWith(".doc") || name.endsWith(".docx")) return "doc";
  return "other";
}

function FileTypeIcon({ record }) {
  const type = detectFileType(record);
  const props = { size: 44, weight: "duotone", className: "text-muted-foreground" };
  switch (type) {
    case "pdf": return <FilePdf {...props} />;
    case "image": return <FileImage {...props} />;
    case "doc": return <FileDoc {...props} />;
    default: return <FileText {...props} />;
  }
}

function formatFileSize(bytes) {
  if (bytes == null) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function MedicalRecordPreviewModal({ record, open, onClose, canDelete, onDelete }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !record) {
      setPreviewUrl(null);
      return;
    }
    setLoading(true);
    api.get(`/medical-records/${record.id}/preview`)
      .then(({ data }) => setPreviewUrl(data.preview_url))
      .catch(() => setPreviewUrl(null))
      .finally(() => setLoading(false));
  }, [record, open]);

  if (!open || !record) return null;

  const fileType = detectFileType(record);

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    if (fileType === "image" && previewUrl) {
      return (
        <img
          src={previewUrl}
          alt={record.title}
          className="max-h-[60vh] w-full object-contain rounded-xl bg-black/5"
        />
      );
    }
    if (fileType === "pdf" && previewUrl) {
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`;
      return (
        <iframe
          src={viewerUrl}
          className="w-full h-[60vh] rounded-xl border border-border"
          title="PDF preview"
        />
      );
    }
    if (fileType === "doc") {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-6 rounded-xl bg-muted/40 border border-dashed border-border">
          <FileDoc size={56} weight="duotone" className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm font-medium text-center">
            Preview not available for this file type.
          </p>
          <p className="text-xs text-muted-foreground mt-1 text-center">
            Open the file externally to view its contents.
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 rounded-xl bg-muted/40 border border-dashed border-border">
        <FileText size={56} weight="duotone" className="text-muted-foreground mb-4" />
        <p className="text-muted-foreground text-sm font-medium text-center">
          Preview not available for this file type.
        </p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-card border border-border p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <FileTypeIcon record={record} />
            <div className="min-w-0">
              <h2 className="font-display font-bold text-lg text-foreground truncate">{record.title}</h2>
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-[0.16em]">
                <span>{record.record_type}</span>
                {record.created_at && <><span>·</span><span>{new Date(record.created_at).toLocaleDateString()}</span></>}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded-full p-1.5 hover:bg-muted transition-colors">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
          {record.file_name && (
            <span className="truncate max-w-[300px]" title={record.file_name}>
              <span className="font-medium text-foreground">File:</span> {record.file_name}
            </span>
          )}
          {record.file_size != null && (
            <span><span className="font-medium text-foreground">Size:</span> {formatFileSize(record.file_size)}</span>
          )}
          {record.content_type && (
            <span className="truncate max-w-[200px]" title={record.content_type}>
              <span className="font-medium text-foreground">Type:</span> {record.content_type}
            </span>
          )}
        </div>

        <div className="mb-5">
          {renderPreview()}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
          {previewUrl && (
            <Button
              asChild
              variant="outline"
              className="rounded-full h-10 gap-1.5"
            >
              <a href={previewUrl} target="_blank" rel="noreferrer">
                <ArrowSquareOut size={ICON_SIZE} /> Open externally
              </a>
            </Button>
          )}
          {canDelete && (
            <Button
              onClick={() => onDelete?.(record.id)}
              variant="destructive"
              className="rounded-full h-10 gap-1.5"
            >
              <Trash size={ICON_SIZE} /> Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}